import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

import {
  buildRecoveryDailyLogBase,
  getRecoveryDailyLog,
  saveRecoveryDailyLog,
} from "../../lib/recoveryDailyLogs";
import { useAuth } from "../../context/AuthContext";

// Import mood SVG assets
import VeryHappyIcon from "../../assets/moods/Smiley-Happy--Streamline-Ultimate 1.svg";
import PleasedIcon from "../../assets/moods/Smiley-Cheerful--Streamline-Ultimate 1.svg";
import MehIcon from "../../assets/moods/Smiley-Unhappy--Streamline-Ultimate 1.svg";
import NotGreatIcon from "../../assets/moods/Smiley-Sad-1--Streamline-Ultimate 1.svg";
import SadIcon from "../../assets/moods/Smiley-Devastated-1--Streamline-Ultimate 1.svg";
import { getTodayId } from "../../utils/date";

// Types
export type MoodKey =
  | "very-happy"
  | "pleased"
  | "meh"
  | "not-great"
  | "sad";

type MoodOption = {
  value: MoodKey;
  label: string;
  icon: string;
  score: number;
};

type DailyRecoveryLog = {
  mood?: MoodKey;
};

const MOOD_OPTIONS: MoodOption[] = [
  { value: "very-happy", label: "Very Happy", icon: VeryHappyIcon, score: 5 },
  { value: "pleased", label: "Pleased", icon: PleasedIcon, score: 4 },
  { value: "meh", label: "Meh", icon: MehIcon, score: 3 },
  { value: "not-great", label: "Not great", icon: NotGreatIcon, score: 2 },
  { value: "sad", label: "Sad", icon: SadIcon, score: 1 },
];

const MOOD_SCORE_MAP: Record<MoodKey, number> = MOOD_OPTIONS.reduce(
  (acc, m) => {
    acc[m.value] = m.score;
    return acc;
  },
  {} as Record<MoodKey, number>
);

export default function MoodCheckin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedWeek: number =
    (location.state as { week?: number })?.week ?? 1;

  const todayId = useMemo(() => {
    const dateFromState = (location.state as { dateId?: string; week?: number })?.dateId;
    return dateFromState ?? getTodayId();
  }, [location.state]);

  const [dailyLog, setDailyLog] = useState<DailyRecoveryLog | null>(null);
  const [isLoadingDailyLog, setIsLoadingDailyLog] = useState(true);
  const [isSavingMood, setIsSavingMood] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedMood = dailyLog?.mood;

  useEffect(() => {
    const loadDailyLog = async () => {
      if (!user) {
        setDailyLog(null);
        setIsLoadingDailyLog(false);
        return;
      }

      try {
        const data = await getRecoveryDailyLog(user.uid, todayId);
        if (data) {
          const moodKey: MoodKey | undefined =
            (data.mood?.key as MoodKey) ?? undefined;
          setDailyLog({ mood: moodKey });
        } else {
          setDailyLog(null);
        }
      } catch {
        setError("Could not load your mood log.");
      } finally {
        setIsLoadingDailyLog(false);
      }
    };

    const raf = requestAnimationFrame(() => {
      loadDailyLog();
    });
    return () => {
      cancelAnimationFrame(raf);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [user, todayId]);

  const fromDashboard = (location.state as { from?: string })?.from === "dashboard";

  const handleClose = () => {
    if (fromDashboard) {
      navigate("/dashboard");
    } else {
      navigate("/recovery", { state: { week: selectedWeek } });
    }
  };

  const handleSelectMood = (value: MoodKey) => {
    setDailyLog((prev) => ({ ...(prev ?? {}), mood: value }));
  };

  const handleConfirmMood = async () => {
    if (!user || !dailyLog?.mood) return;

    setIsSavingMood(true);
    setError(null);
    try {
      const moodKey = dailyLog.mood;
      const score = MOOD_SCORE_MAP[moodKey];

      await saveRecoveryDailyLog(user.uid, todayId, {
        ...buildRecoveryDailyLogBase(user.uid, todayId, selectedWeek),
        mood: { key: moodKey, score },
      });

      setSaved(true);
      closeTimer.current = setTimeout(handleClose, 700);
    } catch {
      setError("Could not save. Please try again.");
      setIsSavingMood(false);
    }
  };

  // ── HIGH QUALITY BRANDED SPINNER (Initial Load) ───────────────────────────
  if (isLoadingDailyLog) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100%',
        minHeight: '-webkit-fill-available', 
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
        pointerEvents: 'all'
      }}>
        {/* Spinner */}
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />

        {/* Branding */}
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200]">
      <motion.button
        type="button"
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 bg-black/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      />

      <motion.div
        className="absolute bottom-0 left-0 right-0 mx-auto w-full max-w-[430px]"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 340, damping: 32, mass: 0.9 }}
      >
        <article className="relative bg-[#fcf9f7] rounded-t-[32px] px-6 pt-6 pb-10 shadow-2xl">
          <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-black/10" />

          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="absolute top-5 right-5 border-none bg-white/70 backdrop-blur inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-white transition-colors"
          >
            <FiX size={20} />
          </button>

          <h2 className="m-0 mb-6 font-semibold text-2xl text-[#0c2624] leading-normal">
            How do you feel today?
          </h2>

          <div className="flex flex-col gap-4">
            {MOOD_OPTIONS.map(({ value, label, icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleSelectMood(value)}
                disabled={isLoadingDailyLog || !user || isSavingMood || saved}
                className={[
                  "w-full rounded-2xl p-3 text-left transition-all border bg-white",
                  "hover:shadow-[0_2px_8px_rgba(0,0,0,0.05)]",
                  selectedMood === value
                    ? "border-[1.5px] border-[#1e5e5b]"
                    : "border-transparent",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                ].join(" ")}
              >
                <div className="flex items-center gap-2.5">
                  <div style={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    backgroundColor: "#BCE9E7",
                  }}
                    className="flex items-center justify-center">
                    <img
                      src={icon}
                      alt={label}
                      className="w-[32px] h-[32px] rounded-full"
                      loading="eager"
                    />
                  </div>
                  <span className="font-semibold text-xl text-[#0c2624] leading-normal">
                    {label}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {error && (
            <p className="mt-4 text-sm font-medium text-red-600">{error}</p>
          )}

          <button
            type="button"
            onClick={handleConfirmMood}
            disabled={
              !user || !selectedMood || isSavingMood || isLoadingDailyLog || saved
            }
            className={[
              "mt-8 w-full h-11 rounded-[60px] font-bold text-base transition-all duration-200",
              saved
                ? "bg-green-600 text-white"
                : "bg-[#1e5e5b] text-white hover:bg-[#164644]",
              "disabled:opacity-60 disabled:cursor-not-allowed",
            ].join(" ")}
          >
            <AnimatePresence mode="wait" initial={false}>
              {saved ? (
                <motion.span
                  key="saved"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Saved!
                </motion.span>
              ) : isSavingMood ? (
                <motion.span
                  key="saving"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center gap-2"
                >
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                    />
                  </svg>
                  Saving…
                </motion.span>
              ) : (
                <motion.span
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Submit
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </article>
      </motion.div>
    </div>
  );
}